import { motion } from "framer-motion";
import { Radar, Radio, Zap, Cpu, Layers } from "lucide-react";
import { MaskedLine } from "@/components/MaskedLine";

const CAPS = [
  {
    n: "01", icon: Radar, title: "ISR SYSTEMS",
    desc: "EO/IR turrets, SAR/GMTI radar and SIGINT payload integration — full-spectrum intelligence, surveillance and reconnaissance suites tailored to the airframe, vehicle or hull.",
    tags: ["EO/IR", "SAR/GMTI", "SIGINT"],
  },
  {
    n: "02", icon: Radio, title: "TACTICAL COMMUNICATIONS",
    desc: "LOS/BLOS SATCOM, secure mesh networking and multi-band datalinks engineered for contested, degraded and denied communications environments.",
    tags: ["SATCOM", "MESH", "DATALINK"],
  },
  {
    n: "03", icon: Zap, title: "ELECTRONIC WARFARE",
    desc: "ESM/ECM suites, radar warning and spectrum-monitoring systems that detect, classify and defeat threats across the electromagnetic battlespace.",
    tags: ["ESM", "ECM", "RWR"],
  },
  {
    n: "04", icon: Cpu, title: "MISSION COMPUTING",
    desc: "Rugged mission computers, video management and sensor-processing hardware — qualified, EMC-tested and built to survive the platform, not the lab.",
    tags: ["RUGGED", "EDGE", "MIL-STD"],
  },
  {
    n: "05", icon: Layers, title: "SENSOR FUSION & C2",
    desc: "Multi-INT correlation fused onto a single tactical picture, with open-architecture software that lets operators task, exploit and disseminate in real time.",
    tags: ["MULTI-INT", "C2", "OPEN-ARCH"],
  },
];

const PLATFORMS = [
  {
    src: "https://images.unsplash.com/photo-1734826460151-641b33dadf50?crop=entropy&cs=srgb&fm=jpg&q=85",
    label: "AIRBORNE ISR", code: "PLTFRM.A", testid: "platform-airborne",
  },
  {
    src: "https://images.unsplash.com/photo-1714255288526-cc155b548aac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxyYWRhciUyMGRpc2glMjBuaWdodHxlbnwwfHx8fDE3ODg3ODQ0OTl8MA&ixlib=rb-4.1.0&q=85",
    label: "GROUND COMMS", code: "PLTFRM.G", testid: "platform-ground",
  },
  {
    src: "https://images.unsplash.com/photo-1521041601476-47a88cecb403?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxtaWxpdGFyeSUyMHNoaXAlMjBwYXRyb2x8ZW58MHx8fHwxNzg4Nzg0NDk5fDA&ixlib=rb-4.1.0&q=85",
    label: "MARITIME PATROL", code: "PLTFRM.M", testid: "platform-maritime",
  },
];

const rowVariants = {
  hidden: { opacity: 0, y: 28 },
  show: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.65, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Corner = ({ className }) => (
  <span aria-hidden="true" className={`pointer-events-none absolute h-3 w-3 border-orange-500/70 ${className}`} />
);

export default function Capabilities() {
  return (
    <section id="capabilities" data-testid="capabilities-section" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <p className="mb-4 text-[10px] tracking-[0.45em] text-orange-500/90 sm:text-xs" data-testid="capabilities-eyebrow">
        {"// 01 — CAPABILITIES"}
      </p>
      <h2
        data-testid="capabilities-heading"
        className="max-w-3xl font-display text-[clamp(2.1rem,5.5vw,4rem)] font-bold uppercase leading-[0.95] tracking-tight text-stone-100"
      >
        <MaskedLine inView>Mission systems,</MaskedLine>
        <MaskedLine inView delay={0.12}>
          <span className="text-stroke-orange">integrated.</span>
        </MaskedLine>
      </h2>
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay: 0.25 }}
        className="mt-6 max-w-2xl text-xs leading-relaxed text-stone-400 sm:text-sm"
        data-testid="capabilities-intro"
      >
        From requirement to first flight, KB/XDU owns the full integration chain — design,
        qualification, installation and sustainment of special mission equipment on crewed and
        uncrewed platforms.
      </motion.p>

      <div className="mt-16 border-t border-orange-500/15">
        {CAPS.map((cap, i) => (
          <motion.div
            key={cap.n}
            custom={i}
            variants={rowVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-70px" }}
            data-testid={`capability-${cap.n}`}
            className="group grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 border-b border-orange-500/15 px-2 py-8 transition-colors duration-300 hover:bg-orange-500/[0.04] sm:grid-cols-[70px_40px_1fr_auto] sm:items-start sm:gap-x-8 sm:px-4 sm:py-10"
          >
            <span className="font-display text-2xl font-bold text-orange-500/60 transition-colors duration-300 group-hover:text-orange-500 sm:text-3xl">
              {cap.n}
            </span>
            <cap.icon
              className="mt-1 hidden h-5 w-5 text-stone-600 transition-colors duration-300 group-hover:text-orange-500 sm:block"
              strokeWidth={1.5}
            />
            <div>
              <h3 className="font-display text-xl font-semibold uppercase tracking-wide text-stone-100 sm:text-2xl">
                {cap.title}
              </h3>
              <p className="mt-3 max-w-xl text-[11px] leading-relaxed text-stone-400 sm:text-xs">
                {cap.desc}
              </p>
            </div>
            <div className="col-span-2 flex flex-wrap gap-2 sm:col-span-1 sm:flex-col sm:items-end">
              {cap.tags.map((t) => (
                <span
                  key={t}
                  className="border border-stone-800 px-2 py-1 text-[9px] tracking-[0.2em] text-stone-500 transition-colors duration-300 group-hover:border-orange-500/40 group-hover:text-orange-500/80"
                >
                  {t}
                </span>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-20 grid gap-6 sm:grid-cols-3">
        {PLATFORMS.map((p, i) => (
          <motion.figure
            key={p.code}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
            data-testid={p.testid}
            className="group relative overflow-hidden border border-orange-500/15"
          >
            <Corner className="left-0 top-0 border-l border-t" />
            <Corner className="right-0 top-0 border-r border-t" />
            <Corner className="bottom-0 left-0 border-b border-l" />
            <Corner className="bottom-0 right-0 border-b border-r" />
            <img
              src={p.src}
              alt={p.label}
              loading="lazy"
              className="h-64 w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0 sm:h-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            <figcaption className="absolute bottom-0 left-0 flex w-full items-center justify-between px-4 py-3">
              <span className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-stone-100">
                {p.label}
              </span>
              <span className="text-[9px] tracking-[0.25em] text-orange-500/80">{p.code}</span>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
}
